// Indexable exploration routes — pre-canned filter sets that turn the public
// browse query into stable, schema-marked, OG-cardable SEO landing pages.
//
// Each entry maps a stable URL slug to a filter set that the existing
// /api/browse endpoint already understands. The SPA route at /explore/:slug
// renders the list using the same UI primitives as /browse, but with a
// hand-crafted heading + description and structured-data injection.
//
// Crawler / share-bot user-agents hitting /explore/:slug are intercepted in
// `apps/web/src/worker/lib/og.ts` and served tailored Open Graph + JSON-LD
// (schema.org ItemList referencing the matching MusicVenue items).
//
// Adding a new exploration: append an entry below + run typecheck. The
// registry is referenced by both the SPA route and the Worker OG renderer
// so both stay in sync from one source.

export type Exploration = {
  slug: string;
  title: string;
  /** One-line lede shown under the title and used as the OG description. */
  description: string;
  /** Query string for /api/browse — already URL-encoded fragment without leading `?`. */
  browseQuery: string;
  /** Optional schema.org `addressLocality` (used in JSON-LD enrichment when present). */
  city?: string;
  /** Optional `addressRegion` — used in JSON-LD. */
  region?: string;
};

export const EXPLORATIONS: Exploration[] = [
  {
    slug: 'venues-in-brisbane',
    title: 'Venues in Brisbane',
    description:
      'Host venues across Brisbane currently open to touring artists on WWAAVE.',
    browseQuery: 'country=AU&q=Brisbane',
    city: 'Brisbane',
    region: 'QLD',
  },
  {
    slug: 'venues-in-byron-bay',
    title: 'Venues near Byron Bay',
    description:
      'Stages within an hour of Byron Bay open to touring artists on WWAAVE.',
    browseQuery: 'country=AU&q=Byron',
    city: 'Byron Bay',
    region: 'NSW',
  },
  {
    slug: 'venues-in-melbourne',
    title: 'Venues in Melbourne',
    description: 'Host venues across Melbourne currently open on WWAAVE.',
    browseQuery: 'country=AU&q=Melbourne',
    city: 'Melbourne',
    region: 'VIC',
  },
  {
    slug: 'venues-in-sydney',
    title: 'Venues in Sydney',
    description: 'Host venues across Sydney currently open on WWAAVE.',
    browseQuery: 'country=AU&q=Sydney',
    city: 'Sydney',
    region: 'NSW',
  },
  {
    slug: 'verified-hosts-australia',
    title: 'Verified host venues, Australia',
    description:
      'Host venues that have completed identity verification on WWAAVE.',
    browseQuery: 'country=AU&verified=1',
  },
  {
    slug: 'founding-cohort-venues',
    title: 'Founding cohort venues',
    description:
      'The first 50 venues to join WWAAVE. Small, hand-onboarded, and shaping how the platform works.',
    browseQuery: 'country=AU&founding=1',
  },
  {
    slug: 'venues-with-a-stipend',
    title: 'Venues offering a stipend',
    description:
      'Hosts that pay artists a stipend for the night, on top of room and board. WWAAVE doesn’t take a cut of any of it.',
    browseQuery: 'country=AU&has_stipend=1',
  },
  {
    slug: 'venues-with-a-bed',
    title: 'Venues with a bed for the night',
    description:
      'Hosts that offer accommodation as part of the exchange. A quiet room and a soft pillow on the road.',
    browseQuery: 'country=AU&sleep=1',
  },
  {
    slug: 'house-show-venues',
    title: 'House-show venues',
    description:
      'Intimate house-concert spaces and small rooms across Australia.',
    browseQuery: 'country=AU&venue_type=house-show',
  },
  {
    slug: 'folk-and-acoustic-venues',
    title: 'Folk and acoustic venues',
    description:
      'Venues that programme folk and acoustic touring artists.',
    browseQuery: 'country=AU&art_form=folk,acoustic',
  },
];

export function explorationBySlug(slug: string): Exploration | null {
  return EXPLORATIONS.find((e) => e.slug === slug) ?? null;
}
